import React, { useMemo, useState } from 'react';
import { getBenefieryView, getInsuredView, getPayerView } from './content';
import { IPolicyPersonDetails } from '../..';
import DynamicWidget from '@components/widgets/dynamic-display/DynamicWidget';
import { makeStyles } from 'tss-react/mui';
import PersonDetailsDrawer from './modify-person-drawer/PersonDetailsDrawer';
import EnhancedButton from '@components/button/EnhancedButton';
import PolicyPersonPopUpShell from './policy-person-popup/PolicyPersonPopUpShell';
import { IAbstractRecord } from '@models';

const useStyles = makeStyles()(() => ({
  actionButton: {
    display: 'inline-block',
    marginRight: '5px',
  },
  spacer: {
    height: '1em',
  },
  buttonContainer: {
    display: 'flex',
    gap: '10px',
  },
}));

const PolicyPersonsTab: React.FC<{
  data: IPolicyPersonDetails;
  lovs?: Record<string, Record<string, string>>;
  planDetails?: IAbstractRecord;
  onDataRefresh?: () => void;
}> = ({ data, lovs, planDetails, onDataRefresh }) => {
  const { classes } = useStyles();

  const [personInfoDrawerOpen, setPersonInfoDrawerOpen] =
    useState<boolean>(false);
  const [personInfoPopupOpen, setPersonInfoPopupOpen] =
    useState<boolean>(false);
  const [section, setSection] = useState<string>('payer');

  const elements = useMemo(() => {
    const items = [];

    const hasPayer = data?.Persons?.payer?.name;
    const hasOwner = data?.Persons?.owner?.name;
    const hasInsured = data?.Persons?.insured?.length > 0;
    const hasBeneficiary = data?.Persons?.beneficiaries?.length > 0;

    if (hasPayer || hasOwner) {
      items.push(
        <DynamicWidget
          key="payer"
          hasFourFields
          collapsibleSectionTitle="Payer"
          view={getPayerView(data.Persons.payer, data.Persons.owner)}
          actions={
            <div className={classes.buttonContainer}>
              <EnhancedButton
                isPrimary
                onClick={() => {
                  setPersonInfoDrawerOpen(true);
                  setSection('payer');
                }}
              >
                Modify Payer
              </EnhancedButton>
              <EnhancedButton
                isPrimary
                onClick={() => {
                  setPersonInfoDrawerOpen(true);
                  setSection('owner');
                }}
              >
                Modify Owner
              </EnhancedButton>
            </div>
          }
        />
      );
    }

    if (hasInsured) {
      if (items.length > 0)
        items.push(<div key="spacer-insured" className={classes.spacer} />);
      items.push(
        <DynamicWidget
          key="insured"
          hasFourFields
          collapsibleSectionTitle="Insured"
          view={getInsuredView(
            data.Persons.insured,
            planDetails?.lineExternalCode
          )}
          actions={
            <EnhancedButton
              isPrimary
              onClick={() => {
                setPersonInfoPopupOpen(true);
                setSection('insured');
              }}
            >
              Modify Insured
            </EnhancedButton>
          }
        />
      );
    }

    if (hasBeneficiary) {
      if (items.length > 0)
        items.push(<div key="spacer-beneficiary" className={classes.spacer} />);
      items.push(
        <DynamicWidget
          key="Beneficiary"
          hasFourFields
          collapsibleSectionTitle="Beneficiary"
          view={getBenefieryView(data.Persons.beneficiaries)}
          actions={
            <EnhancedButton
              isPrimary
              onClick={() => {
                setPersonInfoPopupOpen(true);
                setSection('beneficiary');
              }}
            >
              Modify Beneficiary
            </EnhancedButton>
          }
        />
      );
    }

    return items;
  }, [data]);

  return elements.length > 0 ? (
    <>
      {elements}
      {personInfoDrawerOpen && (
        <PersonDetailsDrawer
          open={personInfoDrawerOpen}
          onClose={() => setPersonInfoDrawerOpen(false)}
          onSuccess={() => {
            onDataRefresh();
          }}
          section={section}
          data={data}
          lovs={lovs}
        />
      )}
      {personInfoPopupOpen && (
        <PolicyPersonPopUpShell
          open={personInfoPopupOpen}
          onClose={() => setPersonInfoPopupOpen(false)}
          onSuccess={() => {
            onDataRefresh();
          }}
          section={section}
          data={data.Persons.insured}
          lovs={lovs}
          lineExternalCode={planDetails.lineExternalCode}
          maxInsured={planDetails.PlanMaxNbInsured}
        />
      )}
    </>
  ) : null;
};

export default PolicyPersonsTab;
